import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';
import { formatINR } from '@/lib/tracks';
import {
  CheckCircle2,
  Sparkles,
  Rocket,
  Share2,
  Gift,
  ArrowRight,
} from 'lucide-react';

/**
 * Full-screen "Plan Activated" celebration — shown right after UTR verification.
 * Uses framer-motion for choreographed entrance and canvas-confetti for the burst.
 */
export function PlanActivatedCelebration({
  planName,
  amount,
  expiresAt,
  orderShortId,
  onPrimary,
  onSecondary,
  onShareReferral,
}) {
  useEffect(() => {
    // Confetti burst — brand red + white + gold for a rich look
    const colors = ['#dc2626', '#ef4444', '#fca5a5', '#ffffff', '#fbbf24'];
    const end = Date.now() + 1200;

    // Center burst
    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.35 },
      colors,
      startVelocity: 45,
      gravity: 0.9,
      scalar: 1.1,
      ticks: 220,
    });

    // Side jets — creates a satisfying dual-cannon feel
    const jet = () => {
      const timeLeft = end - Date.now();
      if (timeLeft <= 0) return;
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors,
      });
      requestAnimationFrame(jet);
    };
    jet();

    return () => {
      // canvas-confetti auto-cleans; nothing else needed.
    };
  }, []);

  const expiryText = React.useMemo(() => {
    if (!expiresAt) return 'active for 1 full year';
    try {
      return `active until ${new Date(expiresAt).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`;
    } catch {
      return 'active for 1 full year';
    }
  }, [expiresAt]);

  return (
    <div
      className="fixed inset-0 z-[200] grid place-items-center overflow-hidden bg-gradient-to-br from-black via-neutral-950 to-red-950/60 px-4 py-10 text-white"
      data-testid="plan-activated-celebration"
    >
      {/* Soft red glow behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full bg-red-600/25 blur-[120px]"
      />

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 120, damping: 16, delay: 0.05 }}
        className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-red-500/30 bg-neutral-950/90 p-8 shadow-[0_0_80px_-20px_rgba(239,68,68,0.6)] backdrop-blur-xl sm:p-10"
      >
        {/* Corner sparkle */}
        <motion.div
          initial={{ opacity: 0, rotate: -20, scale: 0.5 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          transition={{ delay: 0.35, type: 'spring', stiffness: 180 }}
          className="absolute right-6 top-6 text-red-500/70"
          aria-hidden
        >
          <Sparkles className="h-6 w-6" />
        </motion.div>

        {/* Big animated check */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 260, damping: 14 }}
          className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-red-500 to-red-700 shadow-[0_0_50px_rgba(239,68,68,0.55)]"
        >
          <motion.div
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.5, ease: 'easeOut' }}
          >
            <CheckCircle2 className="h-10 w-10 text-white" strokeWidth={2.5} />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mt-6 text-center"
        >
          <div className="text-xs uppercase tracking-[0.28em] text-red-400">
            Payment verified
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
            You're all in.
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            Welcome to <span className="font-semibold text-white">StudyTrack AI</span>.
          </p>
        </motion.div>

        {/* Plan card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
          className="mt-6 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-500/10 via-transparent to-transparent p-5"
          data-testid="celebration-plan-card"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-400">
                Plan activated
              </div>
              <div className="mt-0.5 font-display text-xl font-semibold" data-testid="celebration-plan-name">
                {planName || 'Your plan'}
              </div>
              <div className="mt-1 text-xs text-neutral-400">
                {amount != null ? `${formatINR(amount)} · ` : ''}{expiryText}
              </div>
            </div>
            <div className="grid h-11 w-11 place-items-center rounded-full bg-red-600/20 text-red-400">
              <Rocket className="h-5 w-5" />
            </div>
          </div>
          {orderShortId && (
            <div className="mt-3 font-mono-ui text-[11px] text-neutral-500">
              Order #{orderShortId}
            </div>
          )}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-6 space-y-2.5"
        >
          <Button
            onClick={onPrimary}
            className="h-12 w-full bg-red-600 text-base font-semibold shadow-[0_0_30px_rgba(239,68,68,0.45)] hover:bg-red-500"
            data-testid="celebration-primary-cta"
          >
            <Rocket className="mr-2 h-5 w-5" />
            Take my first mock now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={onSecondary}
              variant="outline"
              className="h-11 border-neutral-700 bg-transparent text-white hover:bg-neutral-900"
              data-testid="celebration-secondary-cta"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Dashboard
            </Button>
            {onShareReferral && (
              <Button
                onClick={onShareReferral}
                variant="outline"
                className="h-11 border-red-500/40 bg-red-500/5 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                data-testid="celebration-refer-cta"
              >
                <Gift className="mr-2 h-4 w-4" />
                Refer & earn
              </Button>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-neutral-500"
        >
          <Share2 className="h-3 w-3" />
          Tell a friend — earn a free chapter each time they subscribe.
        </motion.div>
      </motion.div>
    </div>
  );
}
